import { useEffect, useMemo, useRef, useState } from "react";
import { View, Views } from "react-big-calendar";
import dayjs from "dayjs";
import { CalendarEventItem } from "../components/CalendarView.default";

export interface CalendarRange {
  readonly start: Date;
  readonly end: Date;
}

/**
 * Visible window for a given anchor date + view — mirrors what react-big-calendar renders, with the month
 * grid padded out to whole weeks so leading/trailing days are covered. Used to scope event queries to the
 * window on screen instead of loading everything.
 */
export function rangeFor(date: Date, view: View): CalendarRange {
  const d = dayjs(date);
  switch (view) {
    case Views.DAY:
      return { start: d.startOf("day").toDate(), end: d.endOf("day").toDate() };
    case Views.WEEK:
      return { start: d.startOf("week").toDate(), end: d.endOf("week").toDate() };
    case Views.AGENDA:
      return { start: d.startOf("day").toDate(), end: d.add(30, "day").endOf("day").toDate() };
    case Views.MONTH:
    default:
      return {
        start: d.startOf("month").startOf("week").toDate(),
        end: d.endOf("month").endOf("week").toDate(),
      };
  }
}

export interface UseCalendarEventsOptions<T> {
  /** When false the calendar holds no events and skips loading (e.g. admin view before a company is chosen). */
  readonly enabled?: boolean;
  /** Fetch + map the events for the visible window. */
  readonly load: (range: CalendarRange) => Promise<CalendarEventItem<T>[]>;
  /** Changing this re-runs load (e.g. selected company/department). Window + enabled changes re-run automatically. */
  readonly reloadToken?: string;
}

/**
 * Owns the calendar's controlled date/view, derives the visible window, and (re)loads + maps events whenever
 * the window, `enabled`, or `reloadToken` change. The single home of date-range scoping shared by the admin
 * and personal calendars.
 */
export function useCalendarEvents<T>({
  enabled = true,
  load,
  reloadToken,
}: UseCalendarEventsOptions<T>) {
  const [date, setDate] = useState<Date>(() => new Date());
  const [view, setView] = useState<View>(Views.MONTH);
  const [events, setEvents] = useState<CalendarEventItem<T>[]>([]);
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => rangeFor(date, view), [date, view]);

  // Keep the latest load closure without making it an effect dependency (avoids reload loops on every render).
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) {
      setEvents([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    loadRef.current(range)
      .then((items) => {
        if (!cancelled) setEvents(items);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, date, view, reloadToken]);

  function reload(): void {
    if (!enabled) {
      setEvents([]);
      return;
    }
    setLoading(true);
    loadRef
      .current(range)
      .then((items) => setEvents(items))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  return {
    events,
    loading,
    date,
    view,
    range,
    onNavigate: setDate,
    onView: setView,
    reload,
  };
}

export default useCalendarEvents;
